import { useMemo } from "react";
import { graphql } from "gatsby";
import { GroupAdmission, isArray, SimplifyGatsbyQuery, RichTextType } from "@parksandresorts/core";
import WaitingRoom from "components/hoc/waiting-room";
import SiteConfigurationStore from "components/stores/site-configuration-store";

const GroupAdmissionBlock = ({
  contentful_id,
  purchaseFlowId,
  heroImageForSmallDevices,
  heroImageForLargeDevices,
  useFixedDateStep,
  showOpeningHoursInFixedDateSummary,
  showConcertsInCalendar,
  showScheduledTextBlocksInCalendar,
  bannerText,
  bannerImage,
  bannerLink,
  openBannerLinkInNewTab,
  bannerDarkMode,
  dailyInformationLink,
  hideParkOpeningTimes,
  labelsOverride,
  description,
  hideUpsellStep,
  termsAndConditionsLink,
  priceLevelsWithMarking,
  customColors,
}: SimplifyGatsbyQuery<Queries.GroupAdmissionBlockFragment>) => {
  const bannerProps = {
    bannerText,
    bannerImage: bannerImage?.file.url,
    bannerLink,
    openBannerLinkInNewTab,
    bannerDarkMode,
  };
  const mappedLabels = useMemo(() => {
    if (!labelsOverride || !isArray(labelsOverride.list)) {
      return {};
    }

    return labelsOverride.list.reduce((labels: { [key: string]: string }, item) => {
      if (item.key && item.value) {
        labels[item.key] = item.value;
      }
      return labels;
    }, {});
  }, [labelsOverride]);

  const stepConfigOverride = {
    useFixedDateStep: useFixedDateStep ?? false,
    hideUpsell: hideUpsellStep ?? false,
  };

  return (
    <section className="block block--no-margin" data-contentful-id={contentful_id} style={{ paddingBottom: 0 }}>
      <GroupAdmission
        purchaseFlowId={purchaseFlowId}
        heroImageForSmallDevices={heroImageForSmallDevices}
        heroImageForLargeDevices={heroImageForLargeDevices}
        stepConfigOverride={stepConfigOverride}
        showOpeningHoursInFixedDateSummary={showOpeningHoursInFixedDateSummary}
        showConcertsInCalendar={showConcertsInCalendar}
        bannerProps={bannerProps}
        dailyInformationLink={dailyInformationLink}
        hideParkOpeningTimes={hideParkOpeningTimes}
        labelsOverride={mappedLabels}
        description={description as unknown as RichTextType}
        showScheduledTextBlocksInCalendar={showScheduledTextBlocksInCalendar}
        termsAndConditionsLink={termsAndConditionsLink}
        checkoutVariant={SiteConfigurationStore.siteConfiguration.useNewCheckout ? "Klarna" : "Norce"}
        priceLevelsWithMarking={priceLevelsWithMarking?.map((p) => Number(p)) ?? []}
        customColors={customColors}
      />
    </section>
  );
};

export default WaitingRoom(GroupAdmissionBlock);

export const fragmentGroupAdmissionBlock = graphql`
  fragment GroupAdmissionBlock on ContentfulGroupAdmissionBlock {
    __typename
    contentful_id
    purchaseFlowId
    heroImageForSmallDevices {
      file {
        url
      }
    }
    heroImageForLargeDevices {
      file {
        url
      }
    }
    description {
      raw
      references {
        __typename
        ...RichTextFeature
      }
    }
    useFixedDateStep
    showOpeningHoursInFixedDateSummary
    showConcertsInCalendar
    showScheduledTextBlocksInCalendar
    bannerText {
      raw
    }
    bannerImage {
      file {
        url
      }
    }
    bannerLink {
      ...Link
    }
    openBannerLinkInNewTab
    bannerDarkMode
    dailyInformationLink {
      ...Link
    }
    hideParkOpeningTimes
    labelsOverride {
      list {
        key
        value
      }
    }
    enableWaitingRoom
    connectedProduct {
      ...ProductBlock
    }
    hideUpsellStep
    termsAndConditionsLink {
      ...Link
    }
    priceLevelsWithMarking
    customColors {
      primaryPricingTextColor
      primaryPricingBackgroundColor
      primaryPricingTextColorHover
      primaryPricingBackgroundColorHover
      primaryPricingTextColorSelected
      primaryPricingBackgroundColorSelected
      markedPricingTextColor
      markedPricingBackgroundColor
      markedPricingTextColorHover
      markedPricingBackgroundColorHover
      markedPricingTextColorSelected
      markedPricingBackgroundColorSelected
      primaryPricingSelectedDateMarkerTextColor
      primaryPricingSelectedDateMarkerBackgroundColor
      markedPricingSelectedDateMarkerTextColor
      markedPricingSelectedDateMarkerBackgroundColor
    }
  }
`;
